import type { DashboardDateRange } from "../dashboard";
import type { DashboardAreaKey } from "../dashboard-areas";
import { bar, countBy, inRange, stat, type AreaSummary } from "./build";
import { joinPointKey } from "./point-key";

export type ActivityInput = {
  changes: ReadonlyArray<{ changeDate: Date | string; changeType: string; userId: number }>;
  orderEvents: ReadonlyArray<{ recordedAt: Date | string; eventType: string; userId: number }>;
};

export type UsersInput = ActivityInput & { users: ReadonlyArray<{ role: string }> };

type ActivitySource = Extract<DashboardAreaKey, "inventory" | "orders">;
type ActivityRow = { source: ActivitySource; action: string; userId: number };

function activityRow(source: ActivitySource, change: string, userId: number): ActivityRow {
  return { source, action: joinPointKey(source, change), userId };
}

function activityInPeriod({ changes, orderEvents }: ActivityInput, range: DashboardDateRange): ActivityRow[] {
  return [
    ...changes.filter((row) => inRange(row.changeDate, range)).map((row) => activityRow("inventory", row.changeType, row.userId)),
    ...orderEvents.filter((row) => inRange(row.recordedAt, range)).map((row) => activityRow("orders", row.eventType, row.userId)),
  ];
}

export function summarizeAudit(input: ActivityInput, range: DashboardDateRange): AreaSummary {
  const rows = activityInPeriod(input, range);
  return {
    recordedChanges: stat(rows.length),
    changesBySource: bar(countBy(rows, (row) => row.source)),
  };
}

export function summarizeUsers(input: UsersInput, range: DashboardDateRange): AreaSummary {
  const rows = activityInPeriod(input, range);
  return {
    usersByRole: bar(countBy(input.users, (user) => user.role)),
    usersWithActivity: stat(new Set(rows.map((row) => row.userId)).size),
    activityByAction: bar(countBy(rows, (row) => row.action)),
  };
}
